import { useCallback, useEffect, useRef, useState } from "react";

// Shared color tokens for the app's audio UI.
export const COLORS = {
  bg: "#0f0e0c",
  panel: "#161410",
  rule: "#2a2721",
  yellow: "#e8dc2d",
  red: "#e54b3c",
  recordRed: "#e8634a", // slightly warmer red-orange for the recording CTA
  warn: "#f5b800",
  warnBg: "#2a2410",
  ok: "#4ade80",
  text: "#f4f1ea",
  faint: "#6b6558",
  fainter: "#57533f",
};

export const BAR_COUNT = 14;
const SILENCE_THRESHOLD = 0.03;
const SILENCE_WARN_MS = 2000;

// Pick the best MIME type supported by this browser.
// iOS Safari → audio/mp4 (AAC). Android Chrome / desktop Chrome → audio/webm;codecs=opus.
export const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/aac",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
  ];
  for (const mt of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(mt)) return mt;
    } catch (e) {
      // ignore
    }
  }
  return ""; // let the browser pick
};

export const formatTime = (ms, showCentis = true) => {
  const totalSec = Math.floor(ms / 1000);
  const m = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  if (!showCentis) return `${m}:${s}`;
  const cs = String(Math.floor((ms % 1000) / 10)).padStart(2, "0");
  return `${m}:${s}.${cs}`;
};

// Condense an FFT byte buffer down into BAR_COUNT logarithmically-grouped bins
// so lower frequencies (where voice lives) get more resolution.
const condenseToBars = (freqData) => {
  const bars = new Array(BAR_COUNT).fill(0);
  const n = freqData.length;
  for (let i = 0; i < BAR_COUNT; i++) {
    const startFrac = i / BAR_COUNT;
    const endFrac = (i + 1) / BAR_COUNT;
    const start = Math.max(1, Math.floor(Math.pow(n, startFrac)));
    const end = Math.max(start + 1, Math.floor(Math.pow(n, endFrac)));
    let sum = 0;
    let count = 0;
    for (let j = start; j < end && j < n; j++) {
      sum += freqData[j];
      count++;
    }
    bars[i] = count ? sum / count / 255 : 0;
  }
  return bars;
};

/**
 * useAudioRecorder
 *
 * Encapsulates:
 *  - getUserMedia + MediaRecorder with codec auto-detection
 *  - Web Audio AnalyserNode-driven live level meter (14 log-binned bars)
 *  - Silence detection (peak < SILENCE_THRESHOLD for > SILENCE_WARN_MS)
 *  - Full teardown on stop / unmount
 *
 * Returns a stable object shape so consumers can destructure what they need.
 */
export function useAudioRecorder() {
  const [status, setStatus] = useState("idle"); // idle | recording | recorded
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [mimeType, setMimeType] = useState("");
  const [blobSize, setBlobSize] = useState(0);
  const [levels, setLevels] = useState(() => new Array(BAR_COUNT).fill(0));
  const [silenceWarning, setSilenceWarning] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTsRef = useRef(0);
  const rafRef = useRef(0);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const freqDataRef = useRef(null);
  const lastNonSilentTsRef = useRef(0);

  const closeAudioGraph = useCallback(() => {
    try {
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
      }
      audioCtxRef.current = null;
      freqDataRef.current = null;
    } catch (e) {
      // best-effort cleanup
    }
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      closeAudioGraph();
    };
  }, [closeAudioGraph]);

  const tick = useCallback(() => {
    setElapsedMs(Date.now() - startTsRef.current);

    const analyser = analyserRef.current;
    const buf = freqDataRef.current;
    if (analyser && buf) {
      analyser.getByteFrequencyData(buf);
      const bars = condenseToBars(buf);
      setLevels(bars);
      let peak = 0;
      for (let i = 0; i < bars.length; i++) {
        if (bars[i] > peak) peak = bars[i];
      }
      const now = Date.now();
      if (peak > SILENCE_THRESHOLD) {
        lastNonSilentTsRef.current = now;
        setSilenceWarning(false);
      } else if (now - lastNonSilentTsRef.current > SILENCE_WARN_MS) {
        setSilenceWarning(true);
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const start = useCallback(async () => {
    setError("");
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setAudioBlob(null);
    setBlobSize(0);
    setElapsedMs(0);
    setLevels(new Array(BAR_COUNT).fill(0));
    setSilenceWarning(false);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(
        "Microphone API unavailable. This browser does not support getUserMedia. Try Safari on iOS or Chrome on Android.",
      );
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setError("MediaRecorder is not supported in this browser.");
      return;
    }

    const mt = pickMimeType();
    if (mt === null) {
      setError("MediaRecorder is not supported in this browser.");
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err && err.name ? err.name : "Error";
      let msg = err && err.message ? err.message : String(err);
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg =
          "Microphone permission denied. Enable mic access in your browser settings and try again.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "No microphone found on this device.";
      } else if (name === "NotReadableError") {
        msg = "Microphone is in use by another app.";
      }
      setError(`${name}: ${msg}`);
      return;
    }

    streamRef.current = stream;

    // --- Set up Web Audio analyser for the live level meter ---
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128;
        analyser.smoothingTimeConstant = 0.75;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        sourceNodeRef.current = source;
        analyserRef.current = analyser;
        freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
        if (ctx.state === "suspended") ctx.resume().catch(() => {});
      }
    } catch (e) {
      audioCtxRef.current = null;
      analyserRef.current = null;
      sourceNodeRef.current = null;
      freqDataRef.current = null;
    }

    let recorder;
    try {
      recorder = mt ? new MediaRecorder(stream, { mimeType: mt }) : new MediaRecorder(stream);
    } catch (err) {
      setError(`Failed to start recorder: ${err && err.message ? err.message : String(err)}`);
      stream.getTracks().forEach((t) => t.stop());
      closeAudioGraph();
      return;
    }

    chunksRef.current = [];
    setMimeType(recorder.mimeType || mt || "audio/unknown");

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onerror = (e) => {
      const anyE = e;
      const errName = anyE && anyE.error && anyE.error.name ? anyE.error.name : "RecorderError";
      const errMsg = anyE && anyE.error && anyE.error.message ? anyE.error.message : "Unknown recorder error";
      setError(`${errName}: ${errMsg}`);
    };
    recorder.onstop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      closeAudioGraph();
      setLevels(new Array(BAR_COUNT).fill(0));
      setSilenceWarning(false);
      const type = recorder.mimeType || mt || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      setBlobSize(blob.size);
      if (blob.size === 0) {
        setError("Recording produced no audio data. Try again.");
        setStatus("idle");
        return;
      }
      setAudioBlob(blob);
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setStatus("recorded");
    };

    mediaRecorderRef.current = recorder;
    try {
      recorder.start();
    } catch (err) {
      setError(`Failed to start recording: ${err && err.message ? err.message : String(err)}`);
      stream.getTracks().forEach((t) => t.stop());
      closeAudioGraph();
      return;
    }

    startTsRef.current = Date.now();
    lastNonSilentTsRef.current = Date.now();
    setStatus("recording");
    rafRef.current = requestAnimationFrame(tick);
  }, [tick, closeAudioGraph]);

  const stop = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop();
      } catch (err) {
        setError(`Failed to stop: ${err && err.message ? err.message : String(err)}`);
      }
    }
  }, []);

  const reset = useCallback(() => {
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setAudioBlob(null);
    setElapsedMs(0);
    setBlobSize(0);
    setError("");
    setLevels(new Array(BAR_COUNT).fill(0));
    setSilenceWarning(false);
    setStatus("idle");
  }, []);

  return {
    status,
    elapsedMs,
    error,
    levels,
    silenceWarning,
    mimeType,
    blobSize,
    audioUrl,
    audioBlob,
    start,
    stop,
    reset,
  };
}
